import { CreateCompanyRequest, CreateCompanyResponse } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getAppCheckTokenHeaders, isFirebaseError } from '@/firebase';
import { getFallbackErrorKey, gtmPush, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation } from '../hooks';
import {
    addOrUpdateAllCompaniesCache,
    addOrUpdateAllTeamsCache,
    addOrUpdateCompanyUsersCache
} from '../queries/company-cache';

type UseCreateCompanyActionData = {
    createCompany: (
        data: CreateCompanyRequest
    ) => Promise<CreateCompanyResponse>;
    loading: boolean;
    error: string;
};

/**
 * Creating a company, along with its first team and the caller's own membership
 * of it.
 *
 * Carries no `isAllowed` of its own: this runs during onboarding, where anyone
 * signed in may create the company they are about to own.
 */
export function useCreateCompanyAction(): UseCreateCompanyActionData {
    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error `POST /v3/companies`
    // declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            // Thrown before the request, while minting the App Check token.
            if (isFirebaseError(error)) {
                return t('shared.errors.app_check_failed');
            }

            if (isApiError(error, 'APP_CHECK_FAILED')) {
                return t('shared.errors.app_check_failed');
            }
            if (isApiError(error, 'CANNOT_INVITE_YOURSELF')) {
                return t('actions.create_company.error_cannot_invite_yourself');
            }
            if (isApiError(error, 'DUPLICATE_USERS')) {
                return t('actions.create_company.error_duplicate_users');
            }
            if (isApiError(error, 'USER_NOT_FOUND')) {
                return t('shared.errors.user_not_found');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: (data: CreateCompanyRequest) =>
            // Creating a company is App Check protected, so the token has to be
            // fetched before the request rather than added by an interceptor.
            getAppCheckTokenHeaders()
                .then((headers) =>
                    openApiRef.current.companies.createCompany(data, {
                        headers
                    })
                )
                .then((response) => response.data),
        onSuccess: (data) => {
            addOrUpdateAllCompaniesCache(queryClient, data.data.company);
            addOrUpdateAllTeamsCache(queryClient, data.data.team);
            data.data.users.forEach((user) => {
                addOrUpdateCompanyUsersCache(queryClient, user);
            });

            gtmPush({ event: 'company_created' });

            UIToast.success(t('actions.create_company.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        createCompany: (
            data: CreateCompanyRequest
        ): Promise<CreateCompanyResponse> => mutation.mutateAsync(data),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
